import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import type { ElementPatch, ListStyle, TextContentData, TextElement } from '@/components/canvas/document/publicTypes';
import { elementFillCss, firstStroke } from '@/components/canvas/elements/model';
import { cornerRadiiToCssBorderRadius, resolveCornerRadiiPx } from '@/components/canvas/shared/utils/geometry/cornerRadius';
import { detectBlockKind } from '@/components/canvas/elements/text/utils/textContent';
import { isTextBlockEmpty, isTextDocumentEmpty } from '@/components/canvas/elements/text/model/textPlain';
import { normalizeElementText } from '@/components/canvas/elements/text/model/textNormalize';
import {
  getTextLayoutFromElement,
  isVerticalTextWritingMode,
  resolveBlockTextStyle,
  resolveElementTextList,
  resolveElementPlaceholder,
  resolveTextLineContentEditStyle,
  resolveTextListBlockRowStyle,
  resolveTextListMarkerStyle,
} from '@/components/canvas/elements/text/model/textStyles';
import { isWithinTextEditChrome } from '@/components/canvas/elements/text/overlays/textEditChrome';
import { isTextElementBorderDragZone } from '@/components/canvas/elements/text/utils/textElementBorderZone';
import { serializeEditableToTextDocument } from '@/components/canvas/elements/text/editor/textEditableDom';
import {
  blockPlainTextLength,
  firstTextSelection,
  flatOffsetInBlock,
  positionFromFlatOffset,
  readTextSelectionFromDom,
  restoreTextSelectionToDom,
  selectionIsCollapsed,
  selectionWithFlatOffsets,
} from '@/components/canvas/elements/text/editor/textSelectionModel';
import {
  buildPasteBlocks,
  parseClipboardForTextPaste,
} from '@/components/canvas/elements/text/editor/textPaste';
import {
  applyInlineStyleToSelection,
  deleteBlockBackward,
  getActiveStylesAtSelection,
  insertBlockAfterSelection,
  replaceSelectionWithPastedBlocks,
} from '@/components/canvas/elements/text/editor/textTransactions';
import { registerActiveTextEditorCommands } from '@/components/canvas/elements/text/editor/textEditorSession';
import type {
  InlineTextStyle,
  TextBlock,
  TextBlockType,
  TextDocument,
  TextRun,
  TextSelection,
} from '@/components/canvas/elements/text/model/textDocument';
import { InlineToolbar } from '@/components/canvas/elements/text/overlays/InlineToolbar';

export interface TextEditOverlayProps {
  editingTextId: string | null;
  allElements: TextElement[];
  onCommit: (id: string, update: ElementPatch<'text'>) => void;
  onDismiss: (id: string) => void;
  positionOffset?: { x: number; y: number };
  onSizeChange?: (id: string, patch: ElementPatch<'text'>) => void;
  zoom: number;
}

function patchFromText(
  element: TextElement,
  document: TextDocument,
  finalize = false,
): ElementPatch<'text'> {
  const next = normalizeElementText({
    ...element.data,
    document,
  }, { finalize, shellStyle: element.typography });
  return {
    data: next,
  };
}

function patchFromEditable(
  element: TextElement,
  editable: HTMLElement,
  finalize = false,
): ElementPatch<'text'> {
  const blockKind = detectBlockKind(element.data.document);
  return patchFromText(element, serializeEditableToTextDocument(editable, blockKind), finalize);
}

const EDIT_BLOCK_SELECTOR = '[data-text-edit-block]';
const PLACEHOLDER_SELECTOR = '[data-placeholder]';
const LINE_CONTENT_SELECTOR = '[data-text-line-content]';
const ZERO_WIDTH_SPACE = '\u200B';

function getBlockEditableText(block: HTMLElement): string {
  const lineContent = block.querySelector<HTMLElement>(LINE_CONTENT_SELECTOR);
  const root = lineContent ?? block;
  let text = '';
  root.childNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.matches('[data-placeholder], [data-list-marker]')) return;
      if (el.tagName.toLowerCase() === 'br') return;
    }
    text += node.textContent ?? '';
  });
  return text.split(ZERO_WIDTH_SPACE).join('');
}

function setBlockPlaceholderVisible(block: HTMLElement, visible: boolean): void {
  const placeholder = block.querySelector<HTMLElement>(PLACEHOLDER_SELECTOR);
  if (!placeholder) return;
  placeholder.style.display = visible ? '' : 'none';
}

function isEditableDocumentEmpty(editable: HTMLElement): boolean {
  const blocks = editable.querySelectorAll<HTMLElement>(EDIT_BLOCK_SELECTOR);
  for (const block of blocks) {
    if (getBlockEditableText(block).length > 0) return false;
  }
  return true;
}

function syncPlaceholderVisibility(editable: HTMLElement): void {
  const documentEmpty = isEditableDocumentEmpty(editable);
  editable.querySelectorAll<HTMLElement>(EDIT_BLOCK_SELECTOR).forEach((block) => {
    const blockEmpty = getBlockEditableText(block).length === 0;
    setBlockPlaceholderVisible(block, blockEmpty && documentEmpty);
  });
}

/** Keep a zero-width seed in empty blocks so the caret can anchor after Enter. */
function normalizeEmptyBlockSeeds(editable: HTMLElement): void {
  editable.querySelectorAll<HTMLElement>(EDIT_BLOCK_SELECTOR).forEach((block) => {
    const empty = getBlockEditableText(block).length === 0;
    const lineContent = block.querySelector<HTMLElement>(LINE_CONTENT_SELECTOR);
    if (!lineContent) return;
    const hasSeed = Array.from(lineContent.childNodes).some(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent === ZERO_WIDTH_SPACE,
    );
    if (empty && !hasSeed) {
      lineContent.appendChild(document.createTextNode(ZERO_WIDTH_SPACE));
    } else if (!empty) {
      lineContent.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE && node.textContent === ZERO_WIDTH_SPACE) {
          node.textContent = '';
        }
      });
    }
  });
}

function focusEditableBlock(editable: HTMLElement): void {
  const block = editable.querySelector<HTMLElement>(EDIT_BLOCK_SELECTOR);
  const lineContent = block?.querySelector<HTMLElement>(LINE_CONTENT_SELECTOR);
  focusLineContent(lineContent ?? null);
}

function focusLineContent(lineContent: HTMLElement | null): void {
  const selection = window.getSelection();
  if (!lineContent || !selection) return;
  const range = document.createRange();

  const walker = document.createTreeWalker(lineContent, NodeFilter.SHOW_TEXT);
  let lastContentNode: Text | null = null;
  while (walker.nextNode()) {
    const tn = walker.currentNode as Text;
    if ((tn.parentElement as Element | null)?.closest('[data-placeholder], [data-list-marker]')) {
      continue;
    }
    lastContentNode = tn;
  }

  if (lastContentNode) {
    range.setStart(lastContentNode, lastContentNode.length);
    range.collapse(true);
  } else {
    range.selectNodeContents(lineContent);
    range.collapse(false);
  }
  selection.removeAllRanges();
  selection.addRange(range);
}

function selectionIsInside(editable: HTMLElement, selection: Selection | null): boolean {
  if (!selection || selection.rangeCount === 0) return false;
  const { anchorNode, focusNode } = selection;
  return !!anchorNode && !!focusNode && editable.contains(anchorNode) && editable.contains(focusNode);
}

function selectedTextToolbarRect(editable: HTMLElement): DOMRect | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null;
  if (!editable.contains(selection.anchorNode)) return null;
  const range = selection.getRangeAt(0);
  const rect = range.getBoundingClientRect();
  if (!rect.width && !rect.height) return null;
  return rect;
}

function measureEditableContentWidth(editable: HTMLElement): number {
  const rootRect = editable.getBoundingClientRect();
  let left = rootRect.left;
  let right = rootRect.right;
  editable.querySelectorAll<HTMLElement>(EDIT_BLOCK_SELECTOR).forEach((block) => {
    const rect = block.getBoundingClientRect();
    if (!rect.width && !rect.height) return;
    left = Math.min(left, rect.left);
    right = Math.max(right, rect.right);
  });
  return Math.max(editable.scrollWidth, right - left);
}

function assignStyle(element: HTMLElement, style: CSSProperties): void {
  Object.entries(style).forEach(([key, value]) => {
    if (value == null) return;
    const cssValue =
      key === 'fontSize' && typeof value === 'number'
        ? `${value}px`
        : key === 'letterSpacing' && typeof value === 'number'
          ? `${value}em`
          : String(value);
    element.style[key as never] = cssValue as never;
  });
}

function unorderedMarker(type: ListStyle['unorderedMarker']): string {
  if (type === 'circle') return '○';
  if (type === 'square') return '■';
  return '•';
}

function markerForBlock(blockType: TextBlockType, orderedIndex: number, list: ReturnType<typeof resolveElementTextList>): string | null {
  if (blockType === 'ordered-list-item') return `${orderedIndex}.`;
  if (blockType === 'unordered-list-item') return unorderedMarker(list.unorderedMarker);
  return null;
}

function appendRun(parent: HTMLElement, element: TextElement, block: TextBlock, run: TextRun): void {
  const span = document.createElement('span');
  span.dataset.textRunId = run.id;
  assignStyle(span, resolveBlockTextStyle(element, block.type, run.style));
  span.textContent = run.text;
  parent.appendChild(span);
}

function createEditableBlockElement(element: TextElement, block: TextBlock, orderedIndex: number): HTMLElement {
  const text = element.data;
  const { writingMode } = getTextLayoutFromElement(element);
  const isVertical = isVerticalTextWritingMode(writingMode);
  const list = resolveElementTextList(element.paragraph);
  const documentEmpty = isTextDocumentEmpty(text.document);
  const hasContent = !isTextBlockEmpty(block);
  const showPlaceholder = !hasContent && documentEmpty;
  const placeholderText = resolveElementPlaceholder(text, element.paragraph);
  const marker = markerForBlock(block.type, orderedIndex, list);
  const blockStyle = resolveBlockTextStyle(element, block.type);
  const isList = block.type === 'ordered-list-item' || block.type === 'unordered-list-item';
  const blockEl = document.createElement('div');
  blockEl.dataset.textEditBlock = 'true';
  blockEl.dataset.textBlockId = block.id;
  blockEl.dataset.textBlockType = block.type;
  assignStyle(blockEl, {
    ...resolveTextListBlockRowStyle(blockStyle, list, isList, writingMode),
    position: 'relative',
    minHeight: '1em',
    ...(isVertical
      ? {
          width: 'auto',
          height: '100%',
          minWidth: '1em',
        }
      : {}),
  });

  // Show the marker whenever the document has content (not the initial-empty-placeholder state).
  // This ensures empty items created by Enter still display their bullet / number.
  if (marker && (hasContent || !documentEmpty)) {
    const markerEl = document.createElement('span');
    markerEl.className = 'page-text-list-marker';
    markerEl.dataset.listMarker = 'true';
    markerEl.contentEditable = 'false';
    markerEl.textContent = marker;
    assignStyle(markerEl, resolveTextListMarkerStyle(list, blockStyle));
    blockEl.appendChild(markerEl);
  }

  const contentEl = document.createElement('span');
  contentEl.dataset.textLineContent = 'true';
  assignStyle(contentEl, {
    position: 'relative',
    ...resolveTextLineContentEditStyle(!!element.paragraph.singleLine, writingMode),
  });

  const placeholderEl = document.createElement('span');
  placeholderEl.className = 'page-text-placeholder';
  placeholderEl.dataset.placeholder = 'true';
  placeholderEl.contentEditable = 'false';
  placeholderEl.textContent = placeholderText;
  assignStyle(placeholderEl, {
    ...blockStyle,
    position: 'absolute',
    left: 0,
    top: 0,
    display: showPlaceholder ? '' : 'none',
    opacity: 0.45,
    pointerEvents: 'none',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    wordBreak: 'normal',
    overflowWrap: 'normal',
    lineBreak: 'auto',
  });
  contentEl.appendChild(placeholderEl);

  if (hasContent) {
    block.runs.forEach((run) => appendRun(contentEl, element, block, run));
  } else {
    contentEl.appendChild(document.createTextNode(ZERO_WIDTH_SPACE));
  }

  blockEl.appendChild(contentEl);
  return blockEl;
}

function renderEditableDocument(editable: HTMLElement, element: TextElement): void {
  editable.innerHTML = '';
  let orderedIndex = 0;

  element.data.document.blocks.forEach((block) => {
    const isOrdered = block.type === 'ordered-list-item';
    orderedIndex = isOrdered ? orderedIndex + 1 : 0;
    editable.appendChild(createEditableBlockElement(element, block, orderedIndex));
  });
}

export function TextEditOverlay({
  editingTextId,
  allElements,
  onCommit,
  onDismiss,
  positionOffset,
  onSizeChange,
}: TextEditOverlayProps) {
  const editableRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const composingRef = useRef(false);
  const latestPatchRef = useRef<ElementPatch<'text'> | null>(null);
  const lastBlockBreakAtRef = useRef(0);
  const activeSelectionRef = useRef<TextSelection | null>(null);
  const toolbarSelectionRef = useRef<TextSelection | null>(null);
  const pinnedToolbarRectRef = useRef<DOMRect | null>(null);
  const applyingStyleRef = useRef(false);
  const lastPointerDownInEditableRef = useRef(false);
  const lastPointerDownInToolbarRef = useRef(false);
  const [draftText, setDraftText] = useState<TextContentData | null>(null);
  const [selectionRect, setSelectionRect] = useState<DOMRect | null>(null);
  const [activeStyles, setActiveStyles] = useState<InlineTextStyle>({});
  // Always-current reference to draftText for use inside stable useCallback closures.
  const latestDraftTextRef = useRef<TextContentData | null>(null);
  latestDraftTextRef.current = draftText;
  const latestTextElementRef = useRef<TextElement | null>(null);

  const textEl = useMemo(
    () => (editingTextId ? allElements.find((e) => e.id === editingTextId) ?? null : null),
    [editingTextId, allElements],
  );
  latestTextElementRef.current = textEl;

  const textShellSignature = textEl
    ? [
        textEl.typography.fontFamily,
        textEl.typography.fontSize,
        textEl.typography.fontWeight,
        textEl.typography.fontStyle,
        textEl.typography.color,
        textEl.typography.lineHeight,
        textEl.typography.letterSpacing,
        textEl.paragraph.textAlign,
        textEl.paragraph.verticalAlign,
        textEl.paragraph.writingMode,
        textEl.paragraph.singleLine,
      ].join('|')
    : '';

  const draftElement = useMemo(
    () => (textEl && draftText ? { ...textEl, data: draftText } : textEl),
    [draftText, textEl],
  );

  useEffect(() => {
    if (!textEl) {
      setDraftText(null);
      latestPatchRef.current = null;
      activeSelectionRef.current = null;
      toolbarSelectionRef.current = null;
      pinnedToolbarRectRef.current = null;
      setSelectionRect(null);
      setActiveStyles({});
      return;
    }
    if (!editableRef.current) return;
    const initialText = normalizeElementText(textEl.data, { shellStyle: textEl.typography });
    setDraftText(initialText);
    latestPatchRef.current = { data: initialText };
    activeSelectionRef.current = null;
    toolbarSelectionRef.current = null;
    pinnedToolbarRectRef.current = null;
    setSelectionRect(null);
    renderEditableDocument(editableRef.current, { ...textEl, data: initialText });
    requestAnimationFrame(() => {
      const editable = editableRef.current;
      if (!editable) return;
      normalizeEmptyBlockSeeds(editable);
      syncPlaceholderVisibility(editable);
      editable.focus();
      focusEditableBlock(editable);
      activeSelectionRef.current = readTextSelectionFromDom(editable)
        ?? firstTextSelection(initialText.document);
    });
  }, [textEl?.id]);

  const renderDraftDocument = useCallback((nextText: TextContentData, selection: TextSelection | null) => {
    if (!textEl || !editableRef.current) return;
    renderEditableDocument(editableRef.current, { ...textEl, data: nextText });
    normalizeEmptyBlockSeeds(editableRef.current);
    syncPlaceholderVisibility(editableRef.current);
    restoreTextSelectionToDom(editableRef.current, selection);
  }, [textEl]);

  // 仅在当前元素的壳级样式变化时重算 draft；切换元素时由上方 init effect 负责，
  // 不可依赖 latestDraftTextRef（切换瞬间仍指向上一个元素的 draft）。
  useEffect(() => {
    const editable = editableRef.current;
    const currentText = latestDraftTextRef.current;
    const currentElement = latestTextElementRef.current;
    if (!editable || !currentText || !currentElement) return;
    const selection = readTextSelectionFromDom(editable) ?? activeSelectionRef.current;
    const nextText = normalizeElementText(currentText, {
      shellStyle: currentElement.typography,
    });
    latestPatchRef.current = { data: nextText };
    setDraftText(nextText);
    renderEditableDocument(editable, { ...currentElement, data: nextText });
    normalizeEmptyBlockSeeds(editable);
    syncPlaceholderVisibility(editable);
    restoreTextSelectionToDom(editable, selection);
  }, [textShellSignature]);

  const updateSelectionToolbar = useCallback(() => {
    const editable = editableRef.current;
    if (!editable) {
      pinnedToolbarRectRef.current = null;
      setSelectionRect(null);
      return;
    }

    const liveSelection = window.getSelection();

    // ── Side-effect: keep model-selection refs in sync ──────────────────────
    // readTextSelectionFromDom may return null when typed text lives in raw DOM
    // text nodes that positionFromDomPoint can't locate (e.g. first edit of an
    // empty element before any renderDraftDocument call).  We update the refs
    // only when the parse succeeds, but never block toolbar visibility on it.
    const domSelection = readTextSelectionFromDom(editable);
    if (domSelection && !selectionIsCollapsed(domSelection)) {
      activeSelectionRef.current = domSelection;
      toolbarSelectionRef.current = domSelection;
    } else if (
      selectionIsInside(editable, liveSelection)
      && lastPointerDownInEditableRef.current
      && !lastPointerDownInToolbarRef.current
      && !applyingStyleRef.current
      && (!domSelection || selectionIsCollapsed(domSelection))
    ) {
      if (domSelection) activeSelectionRef.current = domSelection;
      toolbarSelectionRef.current = null;
    }

    // ── Toolbar visibility: driven by the raw DOM selection ─────────────────
    // Using window.getSelection() directly guarantees the toolbar appears
    // whenever the user has selected text inside the editable, regardless of
    // whether the model-selection parse above succeeded.
    const liveNonCollapsed =
      liveSelection &&
      !liveSelection.isCollapsed &&
      editable.contains(liveSelection.anchorNode);

    if (!liveNonCollapsed && !applyingStyleRef.current) {
      pinnedToolbarRectRef.current = null;
      setSelectionRect(null);
      return;
    }

    const rect = selectedTextToolbarRect(editable) ?? pinnedToolbarRectRef.current;
    if (rect) {
      pinnedToolbarRectRef.current = rect;
      setSelectionRect(rect);
    }

    // Compute which styles are active across the current selection so the
    // toolbar can render toggle states correctly.
    const sel = toolbarSelectionRef.current;
    const doc = latestDraftTextRef.current?.document;
    if (sel && doc && !selectionIsCollapsed(sel)) {
      setActiveStyles(getActiveStylesAtSelection(doc, sel));
    } else {
      setActiveStyles({});
    }
  }, [setActiveStyles]);

  const syncDocumentFromDom = useCallback(() => {
    if (!draftElement || !editableRef.current) return null;
    const patch = patchFromEditable(draftElement, editableRef.current);
    latestPatchRef.current = patch;
    if (patch.data) {
      setDraftText(normalizeElementText(
        { ...draftElement.data, ...patch.data } as TextContentData,
        { shellStyle: draftElement.typography },
      ));
    }
    activeSelectionRef.current = (() => {
      const domSelection = readTextSelectionFromDom(editableRef.current);
      const liveSelection = window.getSelection();
      if (!domSelection) return activeSelectionRef.current;
      if (!selectionIsCollapsed(domSelection)) return domSelection;
      if (selectionIsInside(editableRef.current, liveSelection)) return domSelection;
      return activeSelectionRef.current;
    })();
    return patch;
  }, [draftElement]);

  useEffect(() => {
    if (!editingTextId) return;

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      const editable = editableRef.current;
      lastPointerDownInEditableRef.current = !!(editable && target && editable.contains(target));
      lastPointerDownInToolbarRef.current = !!(
        target instanceof Element && target.closest('[data-text-edit-chrome]')
      );
      if (lastPointerDownInToolbarRef.current) {
        applyingStyleRef.current = true;
      }
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('selectionchange', updateSelectionToolbar);
    window.addEventListener('scroll', updateSelectionToolbar, true);
    window.addEventListener('resize', updateSelectionToolbar);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('selectionchange', updateSelectionToolbar);
      window.removeEventListener('scroll', updateSelectionToolbar, true);
      window.removeEventListener('resize', updateSelectionToolbar);
    };
  }, [editingTextId, updateSelectionToolbar]);

  const commit = useCallback(() => {
    if (!editingTextId || !textEl) return;
    const patch = editableRef.current
      ? patchFromEditable(textEl, editableRef.current, true)
      : draftText
        ? { data: normalizeElementText(draftText, { finalize: true, shellStyle: textEl.typography }) }
        : null;
    if (!patch) return;
    onCommit(editingTextId, patch);
  }, [draftText, editingTextId, onCommit, textEl]);

  const dismiss = useCallback(() => {
    if (!editingTextId) return;
    commit();
    onDismiss(editingTextId);
  }, [commit, editingTextId, onDismiss]);

  /**
   * Expand the text element when DOM content overflows.
   * Horizontal text grows downward; vertical-rl text grows leftward so the
   * right edge stays anchored while new columns appear to the left.
   */
  const syncHeightToContentDom = useCallback(() => {
    if (!editingTextId || !onSizeChange || !textEl) return;
    const editable = editableRef.current;
    if (!editable) return;
    const padding = textEl.size.padding;
    const pt = padding?.top ?? 0;
    const pr = padding?.right ?? 0;
    const pb = padding?.bottom ?? 0;
    const pl = padding?.left ?? 0;
    if (isVerticalTextWritingMode(getTextLayoutFromElement(textEl).writingMode)) {
      const neededW = Math.ceil(measureEditableContentWidth(editable)) + pl + pr;
      if (neededW > textEl.size.width) {
        const deltaW = neededW - textEl.size.width;
        onSizeChange(editingTextId, {
          layout: { x: textEl.layout.x - deltaW },
          size: { width: neededW, height: textEl.size.height },
        });
      }
      return;
    }

    const neededH = Math.ceil(editable.scrollHeight) + pt + pb;
    if (neededH > textEl.size.height) {
      onSizeChange(editingTextId, {
        size: { width: textEl.size.width, height: neededH },
      });
    }
  }, [editingTextId, onSizeChange, textEl]);

  const syncSizeToContent = useCallback(() => {
    if (!editingTextId || !textEl || !onSizeChange) return;
    syncDocumentFromDom();
    syncHeightToContentDom();
  }, [editingTextId, textEl, onSizeChange, syncDocumentFromDom, syncHeightToContentDom]);

  const insertBlockAfterCurrent = useCallback(() => {
    const editable = editableRef.current;
    if (!editable || !textEl || !draftText || composingRef.current) return false;

    const selection = readTextSelectionFromDom(editable)
      ?? activeSelectionRef.current
      ?? firstTextSelection(draftText.document);

    const blockKind = detectBlockKind(draftText.document);
    const workingDocument = serializeEditableToTextDocument(editable, blockKind);
    const splitSelection = selection
      ? selectionWithFlatOffsets(workingDocument, selection, editable)
      : selection;

    const { document, selection: nextSelection } = insertBlockAfterSelection(
      workingDocument,
      splitSelection,
      blockKind,
    );
    const nextText = normalizeElementText({
      ...draftText,
      document,
    }, { shellStyle: textEl.typography });
    const patch = patchFromText(textEl, nextText.document);
    activeSelectionRef.current = nextSelection;
    latestPatchRef.current = patch;
    setDraftText(nextText);
    renderDraftDocument(nextText, nextSelection);
    syncHeightToContentDom();
    return true;
  }, [draftText, renderDraftDocument, syncHeightToContentDom, textEl]);

  const handlePaste = useCallback((clipboard: DataTransfer) => {
    const editable = editableRef.current;
    if (!editable || !textEl || !draftText || composingRef.current) return;

    const parsed = parseClipboardForTextPaste(clipboard);
    if (!parsed.lines.some((line) => line.length > 0)) return;

    const blockKind = detectBlockKind(draftText.document);
    const pastedBlocks = buildPasteBlocks(parsed, blockKind);
    const workingDocument = serializeEditableToTextDocument(editable, blockKind);
    const selection = readTextSelectionFromDom(editable)
      ?? activeSelectionRef.current
      ?? firstTextSelection(workingDocument);
    const splitSelection = selection
      ? selectionWithFlatOffsets(workingDocument, selection, editable)
      : selection;

    const { document, selection: nextSelection } = replaceSelectionWithPastedBlocks(
      workingDocument,
      splitSelection,
      pastedBlocks,
    );
    const nextText = normalizeElementText({ ...draftText, document }, { shellStyle: textEl.typography });
    const patch = patchFromText(textEl, nextText.document);
    activeSelectionRef.current = nextSelection;
    latestPatchRef.current = patch;
    setDraftText(nextText);
    renderDraftDocument(nextText, nextSelection);
    syncHeightToContentDom();
  }, [draftText, renderDraftDocument, syncHeightToContentDom, textEl]);

  const handleStructuralLineBreak = useCallback((
    event: Pick<KeyboardEvent | InputEvent, 'preventDefault' | 'stopPropagation' | 'timeStamp'>,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    const eventTime = event.timeStamp || performance.now();
    if (eventTime - lastBlockBreakAtRef.current < 80) return;
    lastBlockBreakAtRef.current = eventTime;

    insertBlockAfterCurrent();
  }, [insertBlockAfterCurrent]);

  /**
   * Intercept Backspace at the start of a list-item block.
   * The browser's native cross-block deletion confuses the DOM because of the
   * non-editable marker span, producing phantom empty items and stale numbering.
   * We handle it ourselves: merge the current block into the previous one and
   * call renderDraftDocument so ordered-list numbers are recalculated.
   *
   * Returns true when we handled the key (caller should preventDefault), false
   * when the browser should handle it normally (cursor not at block start, or
   * not a list item).
   */
  const handleDeleteBackward = useCallback((): boolean => {
    const editable = editableRef.current;
    if (!editable || !textEl || !draftText || composingRef.current) return false;

    const selection = readTextSelectionFromDom(editable)
      ?? activeSelectionRef.current
      ?? firstTextSelection(draftText.document);
    if (!selection || !selectionIsCollapsed(selection)) return false;

    const blockKind = detectBlockKind(draftText.document);
    const workingDocument = serializeEditableToTextDocument(editable, blockKind);
    const splitSelection = selectionWithFlatOffsets(workingDocument, selection, editable);

    const focusBlock = workingDocument.blocks.find((b) => b.id === splitSelection.focus.blockId);
    if (!focusBlock) return false;

    // Only intercept list items — the non-editable marker span causes the
    // browser to misbehave; plain paragraph blocks merge cleanly on their own.
    const isListItem =
      focusBlock.type === 'ordered-list-item' || focusBlock.type === 'unordered-list-item';
    if (!isListItem) return false;

    const focusOffset = flatOffsetInBlock(focusBlock, splitSelection.focus);
    if (focusOffset !== 0) return false;

    const result = deleteBlockBackward(workingDocument, splitSelection);
    if (!result) return false;

    const nextText = normalizeElementText(
      { ...draftText, document: result.document },
      { shellStyle: textEl.typography },
    );
    const patch = patchFromText(textEl, nextText.document);
    activeSelectionRef.current = result.selection;
    latestPatchRef.current = patch;
    setDraftText(nextText);
    renderDraftDocument(nextText, result.selection);
    return true;
  }, [draftText, editingTextId, renderDraftDocument, textEl]);

  const applyInlineStyleCommand = useCallback((stylePatch: InlineTextStyle) => {
    const editable = editableRef.current;
    if (!textEl || !editable) return;
    applyingStyleRef.current = true;

    const sel = readTextSelectionFromDom(editable);
    if (!sel || selectionIsCollapsed(sel)) {
      applyingStyleRef.current = false;
      return;
    }

    // Choose the working document:
    //
    // • Normal path (second edit, or first edit after typing): draftText has the
    //   correct text and its run IDs match the DOM run spans produced by the last
    //   renderDraftDocument call.  Using draftText preserves those IDs so that
    //   flatOffsetInBlock can resolve sel.anchor.runId and sel.focus.runId exactly,
    //   which is required for correct selection restoration after styling.
    //
    // • First-edit path (element just created, user typed but draftText still has
    //   empty runs because syncDocumentFromDom hasn't propagated yet): fall back to
    //   re-reading the document from the DOM.  In this case sel.runId is always ''
    //   (raw text node, no run spans), so flatOffsetInBlock uses the flat-offset
    //   Math.min path and the fresh block content is required to get a non-zero
    //   blockPlainTextLength.
    const draftAnchorBlock = draftText?.document.blocks.find((b) => b.id === sel.anchor.blockId);
    const usesDraftText = !!draftAnchorBlock && blockPlainTextLength(draftAnchorBlock) > 0;

    let workingText: TextContentData;
    if (usesDraftText) {
      workingText = draftText!;
    } else {
      const blockKind = detectBlockKind(textEl.data.document);
      const freshDocument = serializeEditableToTextDocument(editable, blockKind);
      workingText = normalizeElementText(
        { ...(draftText ?? textEl.data), document: freshDocument },
        { shellStyle: textEl.typography },
      );
    }

    const origAnchorBlock = workingText.document.blocks.find((b) => b.id === sel.anchor.blockId);
    const origFocusBlock = workingText.document.blocks.find((b) => b.id === sel.focus.blockId);
    if (!origAnchorBlock || !origFocusBlock) {
      applyingStyleRef.current = false;
      return;
    }
    const origAnchorFlat = flatOffsetInBlock(origAnchorBlock, sel.anchor);
    const origFocusFlat = flatOffsetInBlock(origFocusBlock, sel.focus);
    const anchorBlockIdx = workingText.document.blocks.indexOf(origAnchorBlock);
    const focusBlockIdx = workingText.document.blocks.indexOf(origFocusBlock);
    const isForward =
      anchorBlockIdx < focusBlockIdx
      || (anchorBlockIdx === focusBlockIdx && origAnchorFlat <= origFocusFlat);
    const [startBlockId, startFlat, endBlockId, endFlat] = isForward
      ? [sel.anchor.blockId, origAnchorFlat, sel.focus.blockId, origFocusFlat]
      : [sel.focus.blockId, origFocusFlat, sel.anchor.blockId, origAnchorFlat];

    const { document } = applyInlineStyleToSelection(workingText.document, sel, stylePatch);
    const nextText = normalizeElementText({ ...workingText, document }, { shellStyle: textEl.typography });

    // Map the pre-snapshot flat offsets into the final normalised document.
    // Using the original document's flat offsets instead of applyInlineStyleToSelection's
    // intermediate nextSelection avoids any run-ID invalidation caused by either
    // normalisation pass (stripShellEquivalentStyle may merge adjacent runs).
    const nextSelection: TextSelection | null = (() => {
      const nextStartBlock = nextText.document.blocks.find((b) => b.id === startBlockId);
      const nextEndBlock = nextText.document.blocks.find((b) => b.id === endBlockId);
      if (!nextStartBlock || !nextEndBlock) return null;
      return {
        anchor: positionFromFlatOffset(nextStartBlock, startFlat),
        focus: positionFromFlatOffset(nextEndBlock, endFlat),
      };
    })();
    const patch = patchFromText(textEl, nextText.document);
    activeSelectionRef.current = nextSelection;
    toolbarSelectionRef.current = nextSelection;
    latestPatchRef.current = patch;
    setDraftText(nextText);
    renderDraftDocument(nextText, nextSelection);

    requestAnimationFrame(() => {
      const currentEditable = editableRef.current;
      if (!currentEditable || !nextSelection) {
        applyingStyleRef.current = false;
        lastPointerDownInToolbarRef.current = false;
        return;
      }
      currentEditable.focus({ preventScroll: true });
      restoreTextSelectionToDom(currentEditable, nextSelection);
      const rect = selectedTextToolbarRect(currentEditable) ?? pinnedToolbarRectRef.current;
      if (rect) {
        pinnedToolbarRectRef.current = rect;
        setSelectionRect(rect);
      }
      // Refresh active-style indicators with the post-apply selection.
      setActiveStyles(getActiveStylesAtSelection(nextText.document, nextSelection));
      applyingStyleRef.current = false;
      lastPointerDownInToolbarRef.current = false;
      updateSelectionToolbar();
    });
  }, [draftText, renderDraftDocument, textEl, updateSelectionToolbar]);

  useEffect(() => {
    if (!textEl) return;
    return registerActiveTextEditorCommands(applyInlineStyleCommand);
  }, [applyInlineStyleCommand, textEl]);

  useEffect(() => {
    if (!editingTextId) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (overlayRef.current?.contains(target)) return;
      if (isWithinTextEditChrome(target)) return;
      dismiss();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [dismiss, editingTextId]);

  if (!textEl) return null;

  const { typography, paragraph, writingMode } = getTextLayoutFromElement(textEl);
  const padding = textEl.size.padding;
  const stroke = firstStroke(textEl.appearance);
  const rect = {
    left: (positionOffset?.x ?? 0) + textEl.layout.x,
    top: (positionOffset?.y ?? 0) + textEl.layout.y,
    width: textEl.size.width,
    height: textEl.size.height,
  };

  const editableStyle: CSSProperties = {
    outline: 'none',
    width: '100%',
    height: '100%',
    minHeight: '100%',
    minWidth: 0,
    ...resolveTextLineContentEditStyle(!!textEl.paragraph.singleLine, writingMode),
    textAlign: paragraph.textAlign,
    fontFamily: typography.fontFamily,
    fontSize: typography.fontSize,
    fontWeight: typography.fontWeight,
    fontStyle: typography.fontStyle,
    color: typography.color,
    lineHeight: typography.lineHeight,
    letterSpacing: typography.letterSpacing,
    writingMode,
    position: 'relative',
  };

  return (
    <>
      <div
        ref={overlayRef}
        className="absolute z-[70]"
        style={{
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
          pointerEvents: 'auto',
        }}
        onMouseDown={(e) => {
          if (isTextElementBorderDragZone(e)) return;
          e.stopPropagation();
        }}
        onPointerDown={(e) => e.stopPropagation()}
        data-text-edit-overlay
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            paddingTop: padding?.top ?? 8,
            paddingRight: padding?.right ?? 8,
            paddingBottom: padding?.bottom ?? 8,
            paddingLeft: padding?.left ?? 8,
            backgroundColor: elementFillCss(textEl),
            borderWidth: stroke?.width,
            borderColor: stroke?.color,
            borderStyle: stroke?.style,
            borderRadius: cornerRadiiToCssBorderRadius(resolveCornerRadiiPx(textEl)),
            overflow: 'hidden',
          }}
        >
          <div
            ref={editableRef}
            contentEditable
            suppressContentEditableWarning
            data-text-editor
            style={editableStyle}
            onPointerDown={() => {
              lastPointerDownInEditableRef.current = true;
            }}
            onCompositionStart={() => {
              composingRef.current = true;
            }}
            onCompositionEnd={() => {
              window.setTimeout(() => {
                composingRef.current = false;
                if (editableRef.current) {
                  normalizeEmptyBlockSeeds(editableRef.current);
                  // Update placeholder visibility now that composition is done.
                  syncPlaceholderVisibility(editableRef.current);
                }
                syncSizeToContent();
              }, 0);
            }}
            onPaste={(e) => {
              if (composingRef.current) return;
              e.preventDefault();
              e.stopPropagation();
              handlePaste(e.clipboardData);
            }}
            onBeforeInput={(e) => {
              const inputEvent = e.nativeEvent as InputEvent;
              if (inputEvent.inputType === 'insertFromPaste' && !composingRef.current) {
                e.preventDefault();
                return;
              }
              // Do NOT mutate the DOM during IME composition — any DOM change inside
              // a composing session (e.g. toggling placeholder display) can disrupt the
              // IME and cause the browser to abort composition / lose focus.
              if (editableRef.current && !composingRef.current) {
                syncPlaceholderVisibility(editableRef.current);
              }
              if (
                (inputEvent.inputType === 'insertParagraph'
                  || inputEvent.inputType === 'insertLineBreak')
                && !composingRef.current
              ) {
                handleStructuralLineBreak(inputEvent);
                return;
              }
            }}
            onInput={(e) => {
              if (((e.nativeEvent as InputEvent).isComposing ?? false) || composingRef.current) return;
              if (editableRef.current) normalizeEmptyBlockSeeds(editableRef.current);
              syncSizeToContent();
            }}
            onMouseUp={updateSelectionToolbar}
            onKeyUp={updateSelectionToolbar}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                dismiss();
              }
              if (e.key === 'Enter' && !e.shiftKey && !composingRef.current) {
                handleStructuralLineBreak(e);
              }
              if (
                e.key === 'Backspace'
                && !e.metaKey && !e.altKey && !e.ctrlKey
                && !composingRef.current
              ) {
                if (handleDeleteBackward()) {
                  e.preventDefault();
                }
              }
              if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
                e.stopPropagation();
              }
            }}
          />
        </div>
      </div>
      {createPortal(
        <InlineToolbar
          selectionRect={selectionRect}
          activeStyles={activeStyles}
          onInlineStyleCommand={applyInlineStyleCommand}
          onInteractionStart={() => {
            applyingStyleRef.current = true;
          }}
        />,
        document.body,
      )}
    </>
  );
}
